import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';

import { SHOW_MODAL_ON_INACTION_SECONDS } from '../../src/env';
import MarketingModalLayout from './Layout';

export interface MarketingModalProps {
  buttonText?: string;
  customUrl?: string;
  disclosure?: string;
  guid: string;
  header: string;
  highlightedCta: string;
  id: number;
  isGdprLead?: boolean;
  modalType: 'standard_offer' | 'standard_subscription' | 'lead_capture' | 'custom_url';
  offerLink: string;
  subtitle: string;
  title: string;
  trackingProps: { [key: string]: string };
}

const updateWindowEvents = (value: () => void | null) => {
  window.onclick = value;
  window.onkeypress = value;
  window.ontouchstart = value;
  window.onmousemove = value;
  window.onmousedown = value;
  window.onscroll = value;
};

const MarketingModal = (props: MarketingModalProps) => {
  const { id } = props;

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [hasClosedModal, setHasClosedModal] = useState<boolean>(false);

  const timerRef = useRef<ReturnType<typeof setTimeout>>(null);

  const showModal = useCallback(() => {
    if (!hasClosedModal && !isOpen) {
      clearTimeout(timerRef.current);
      setIsOpen(true);
      updateWindowEvents(null);
      /* eslint-disable no-undef */
      // @ts-expect-error TODO: import ahoy instead of using global instance?
      trackEvent('Views', 'Marketing Modal view', {
        identifier: id,
        name: `marketing_modal`,
        page: window.location.pathname,
      });
    }

    /* eslint-enable no-undef */
  }, [id, hasClosedModal, isOpen]);

  const closeModal = () => {
    setIsOpen(false);
    setHasClosedModal(true);
  };

  const resetTime = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(showModal, SHOW_MODAL_ON_INACTION_SECONDS);
  }, [showModal]);

  useEffect(() => {
    if (!hasClosedModal && !isOpen) {
      resetTime();
      updateWindowEvents(resetTime);
      // show when mouse leaves page
      document.addEventListener('mouseleave', showModal);
    }

    if (isOpen || hasClosedModal) {
      document.removeEventListener('mouseleave', showModal);
    }

    return () => {
      document.removeEventListener('mouseleave', showModal);
    };
  }, [showModal, resetTime, hasClosedModal, isOpen]);

  return (
    <BrowserRouter>
      <MarketingModalLayout {...props} isOpen={isOpen} closeModal={closeModal} />
    </BrowserRouter>
  );
};

export default MarketingModal;
